import React, { type JSX } from "react";
import SVG from "react-inlinesvg";
import Modal from "react-modal";
import { t } from "ttag";

import iconCircleClose from "../../../../img/icons/circle-close.svg";
import {
    IAddress,
    IBillingAddress,
    IShippingAddress,
} from "../../../models/address.interfaces";
import { focusElement } from "../../../utils/keyboardFocus";
import { IReduxFormState } from "../reducers.interfaces";

import styles from "./AddressSuggestionModal.module.scss";

interface IProps {
    form: IReduxFormState;
    onContinue: () => void;
    onEdit: () => void;
    showAddressWarningModal: boolean;
    addressPrefix: string;
}

interface IState {}

// TODO(ssr, LEVEL 4 out of 10): Migrate this component from class-style to functional-style. Rated LOE as 4/10 due to 143 LOC with modal integration (react-modal), address field mapping, keyboard focus handling, and simple button handlers. Straightforward migration with moderate complexity.
export class AddressWarningModal extends React.Component<IProps, IState> {
    public state: IState = {};

    render() {
        const modalStyle: Modal.Styles = {
            content: {
                position: "absolute",
                top: "50%",
                bottom: "auto",
                left: "50%",
                right: "auto",
                transform: "translate(-50%, -50%)",
                width: "400px",
                padding: "30px",
                overflow: "auto",
            },
        };

        const formAddress: Partial<IAddress> = [
            "first_name",
            "last_name",
            "line1",
            "line2",
            "line4",
            "state",
            "postcode",
        ].reduce((address, field) => {
            const key = (this.props.addressPrefix + "_" + field) as keyof (
                | IShippingAddress
                | IBillingAddress
            );
            return { ...address, [field]: this.props.form[key] };
        }, {});

        let givenLine2: JSX.Element | null = null;
        if (formAddress.line2) {
            givenLine2 = (
                <span className="address-suggestion-model__address-line">
                    {formAddress.line2}
                </span>
            );
        }

        return (
            <Modal
                aria={{ modal: true }}
                contentLabel={t`Address Verification`}
                style={modalStyle}
                isOpen={this.props.showAddressWarningModal}
                onRequestClose={() => {
                    this.props.onEdit();
                }}
                onAfterOpen={() => {
                    focusElement(`.${styles.closeButton}`);
                }}
                role="dialog"
            >
                <button
                    className={styles.closeButton}
                    aria-label={t`close`}
                    onClick={() => {
                        this.props.onEdit();
                    }}
                >
                    <SVG
                        aria-hidden="true"
                        className={styles.closeIcon}
                        src={iconCircleClose}
                        title={t`Close Icon`}
                    />
                </button>
                <div className="address-warning-model">
                    <h2 className={styles.header}>
                        {t`Please verify your address`}
                    </h2>
                    {/* TODO(i18n): Remove HTML from translation string - split into two elements or use jt for proper formatting */}
                    <p
                        dangerouslySetInnerHTML={{
                            __html: t`Our system doesn't recognize this address.<br />Please confirm:`,
                        }}
                    ></p>
                    <ul>
                        <li>{t`The house number is accurate`}</li>
                        <li>{t`There are no special characters`}</li>
                        <li>{t`The city name is not abbreviated`}</li>
                    </ul>
                    {/* TODO(i18n): Hard-coded formatting assumes word order and punctuation (comma, space). Address formatting varies by locale and should be configurable */}
                    <p className="address-suggestion-model__address">
                        <span className={styles.addressLine}>
                            {formAddress.first_name} {formAddress.last_name}
                        </span>
                        <span className={styles.addressLine}>
                            {formAddress.line1}
                        </span>
                        {givenLine2}
                        <span className={styles.addressLine}>
                            {formAddress.line4}, {formAddress.state}{" "}
                            {formAddress.postcode}
                        </span>
                    </p>
                    <button
                        className={`${styles.continueButton} button`}
                        onClick={this.props.onContinue}
                    >
                        <span>{t`CONTINUE AS IS`}</span>
                    </button>
                    <button
                        className={`${styles.editButton} button button--secondary`}
                        onClick={this.props.onEdit}
                    >
                        <span>{t`EDIT`}</span>
                    </button>
                </div>
            </Modal>
        );
    }
}
